import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import './sampleIn.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { ExportMenu, type ExportMenuHandle } from '../../components/AppToolbar/ExportMenu';
import { LoadTimeChip } from '../../components/LoadTimeChip';
import { LoaderSymbol } from '../../components/loading';
import { useNotify } from '../../components/notifications';
import { useSaveFlow, useConfirm } from '../../components/confirm';
import { useHotkeys, formatCombo, type HotkeyDef } from '../../components/hotkeys';
import { useAuthStore } from '../../auth/store';
import { useNavPermission } from '../../hooks/useNavPermission';

// ─────────────────────────────────────────────────────────────────────────────
// Shared Barcode Sample Custody scan form (Sample Out / Sample Disposal).
// Same prototype chrome as Sample In — top+bottom action bars, amber Barcode
// Scan band, blue "Barcode Transaction" header, the working grid — driven by a
// per-form config so Out and Disposal are thin wrappers.
// (CLAUDE.md §18.11b — legacy SampleInOutController.)
// ─────────────────────────────────────────────────────────────────────────────

const REQ_FIELD: CSSProperties = {
  // 28px fixed height (user request 2026-09-19) — box-sizing:border-box so the
  // 1px border + padding are inside the 28px.
  border: '1px solid #ff8c42', borderRadius: 4, padding: '0 10px',
  height: 'var(--ai-control-h, 28px)', boxSizing: 'border-box',
  fontSize: 13, background: '#fffbe7', color: 'var(--ai-ink)',
};
/** One scanned custody line. */
export interface CustodyRow {
  id:             string;
  bottle:         string;
  from:           string;
  to:             string;
  scannedAt:      string;   // ISO — display; server stamps the real timestamp (or the looked-up DateHandled for Location)
  by:             string;
  haz:            boolean;
  mode:           string;
  deplete:        boolean;
  wasteDrum:      string;
  storage?:       string;   // Sample Location
  user?:          string;   // Sample Location
  // save payload
  sampleLoginOid: string | null;
  jobOid:         string | null;
  client:         string | null;
}

/** Shape of the resolve endpoints' `data` (superset across In/Out/Disposal/Location). */
export interface CustodyResolveDto {
  ok:             boolean;
  error:          string | null;
  sampleBottleId: string | null;
  jobId?:         string | null;
  client?:        string | null;
  sampleLoginOid?: string | null;
  jobOid?:        string | null;
  from?:          string | null;
  hazardous?:     boolean;
  storage?:       string | null;   // Location
  user?:          string | null;   // Location
  mode?:          string | null;   // Location
  dateHandled?:   string | null;   // Location
  handledByName?: string | null;   // Location
}

export interface CustodyFormConfig {
  pageId:         string;   // navData id (drives FormTitle icon/colour)
  /** Exported Excel file name (user request 2026-09-25 — a readable "SampleOut"
   *  rather than the pageId "sample-out"). Falls back to pageId when unset. */
  exportFilename?: string;
  titleOverride?: string;   // heading text override (e.g. "Sample Disposal History")
  breadcrumbLeaf: string;   // "Sample Out"
  subtitle:       string;   // e.g. "Scan bottle barcodes to check them out"
  actionLabel:    string;   // "Save" | "Dispose"
  confirmTitle:   string;   // "Save" | "Dispose"
  confirmMessage: string;
  successVerb:    string;   // "Saved" | "Disposed"
  formName:       string;   // audit form name
  columns?:       AppColumn<CustodyRow>[];   // static columns (or use buildColumns)
  resolveUrl:     string;
  saveUrl?:       string;   // omitted for read-only (Sample Location)
  emptyPrompt:    string;   // grid empty text
  /** Default grid sort (ALPACA parity — every custody list sorts SampleBottleID ascending). */
  initialSort?:   { key: string; dir: 'asc' | 'desc' };
  /** Read-only "scan to find" form (Sample Location / Disposal History): no Save, no Unlink. */
  readOnly?:      boolean;
  /** Route (e.g. "/m/sample-disposal-history") for the top-left History button. */
  historyRoute?:  string;
  buildRow:       (r: CustodyResolveDto, ctx: { userName: string; nowIso: string }) => CustodyRow;
  buildScan?:     (row: CustodyRow) => Record<string, unknown>;
  /** Multi-row scan (Disposal History): one scan returns MANY rows. Overrides buildRow. */
  buildRows?:     (r: CustodyResolveDto, ctx: { userName: string; nowIso: string }) => CustodyRow[];
  /** Editable/interactive columns (e.g. the Deplete checkbox) — given a row updater. */
  buildColumns?:  (u: { updateRow: (id: string, patch: Partial<CustodyRow>) => void }) => AppColumn<CustodyRow>[];
}

export default function CustodyScanForm({ config }: { config: CustodyFormConfig }) {
  const [barcode,  setBarcode]  = useState('');
  const [rows,     setRows]     = useState<CustodyRow[]>([]);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [scanning, setScanning] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [loadMs,   setLoadMs]   = useState<number | null>(null);
  const [loading,  setLoading]  = useState(true);

  const barcodeRef = useRef<HTMLInputElement>(null);
  // Ref to the grid-header "Export to" menu so Alt+X opens it (the labelled Export
  // button lives in the grid's headerLeft; the grid's built-in icon is hidden).
  const exportRef = useRef<ExportMenuHandle>(null);
  const notify   = useNotify();
  const runSave  = useSaveFlow();
  const confirm  = useConfirm();
  const navigate = useNavigate();
  const userName = useAuthStore(s => s.user?.name ?? '');
  // MAC write gate (ALPACA parity): Save is enabled only when the user's role is
  // granted Write on this form's NavigationItem (config.formName === the ALPACA
  // NavigationId). Server enforces the same on POST (§14b.5). Read-only forms
  // never render Save, so the resolved read right is harmless there.
  const perm = useNavPermission(config.formName);
  const canWrite = perm.canWrite || perm.isAdmin;
  // Read-only access (ALPACA parity): on a WRITE form (Sample Out / Disposal) a
  // user without Write may VIEW but not enter data — the scan input, Unlink and
  // Save are disabled. A read-only-CONFIG form (Sample Location / Disposal
  // History) is a scan-to-find LOOKUP, not data entry, so scanning stays enabled
  // for everyone there.
  const entryDisabled = !config.readOnly && !canWrite;

  // In-place row patch (e.g. the Deplete checkbox toggling a row's mode).
  const updateRow = useCallback((id: string, patch: Partial<CustodyRow>) => {
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
  }, []);
  const columns = useMemo(
    () => (config.buildColumns ? config.buildColumns({ updateRow }) : (config.columns ?? [])),
    [config, updateRow]);

  // No async load, but keep the top load-time chip for parity with the other forms.
  useEffect(() => {
    const t0 = performance.now();
    setLoadMs(performance.now() - t0);
    setLoading(false);
    setTimeout(() => barcodeRef.current?.focus(), 50);
  }, []);

  // Auto-fires when the barcode field commits (blur / Enter / scanner terminator),
  // NOT only on Enter — matching ALPACA's ImmediatePostData editor. The busy guard
  // stops a blur + Enter double-add.
  const scanBusy = useRef(false);
  const scan = useCallback(async () => {
    const code = barcode.trim();
    if (!code || scanBusy.current) return;
    if (rows.some(r => r.bottle.toUpperCase() === code.toUpperCase())) {
      notify.error("This 'Bottle ID' exists already. Please enter a new 'Bottle ID'.");
      setBarcode('');
      return;
    }
    scanBusy.current = true;
    setScanning(true);
    try {
      const res = await api.post(config.resolveUrl, { bottleId: code });
      const r = (res.data as { data: CustodyResolveDto }).data;
      if (!r.ok) { notify.error(r.error ?? "Please enter a valid 'Bottle ID'."); setBarcode(''); return; }
      const ctx = { userName, nowIso: new Date().toISOString() };
      const added = config.buildRows ? config.buildRows(r, ctx) : [config.buildRow(r, ctx)];
      setRows(prev => [...added, ...prev]);
      setBarcode('');
    } catch {
      notify.error('Scan failed. Please try again.');
    } finally {
      scanBusy.current = false;
      setScanning(false);
      barcodeRef.current?.focus();
    }
  }, [barcode, rows, userName, notify, config]);

  const unlink = useCallback(async () => {
    if (selected.size === 0) { notify.warning('Select a scanned row to unlink.'); return; }
    const ok = await confirm.confirm({
      title: 'Unlink', message: `Remove ${selected.size} scanned bottle(s) from this batch?`, tone: 'danger',
    });
    if (!ok) return;
    setRows(prev => prev.filter(r => !selected.has(r.id)));
    setSelected(new Set());
    notify.success("Sample 'Bottle ID' unlinked successfully.");
  }, [selected, confirm, notify]);

  const save = useCallback(() => {
    if (!config.saveUrl || !config.buildScan) return;   // read-only (Sample Location)
    if (rows.length === 0) { notify.error("Please scan the sample 'Bottle ID'."); return; }
    const saveUrl = config.saveUrl;
    const buildScan = config.buildScan;
    setSaving(true);
    runSave({
      formName: config.formName,
      auditAction: 'create',
      confirmTitle: config.confirmTitle,
      confirmMessage: config.confirmMessage,
      save: async () => {
        await api.post(saveUrl, { scans: rows.map(buildScan) });
        setRows([]);
        setSelected(new Set());
      },
      successMessage: `${config.successVerb} ${rows.length} bottle(s).`,
    }).finally(() => setSaving(false));
  }, [rows, runSave, notify, config]);

  const cancel = useCallback(async () => {
    if (rows.length > 0) {
      const ok = await confirm.confirm({ title: 'Cancel', message: `Discard ${rows.length} scanned bottle(s)?`, tone: 'danger' });
      if (!ok) return;
    }
    setRows([]); setSelected(new Set()); setBarcode('');
  }, [rows, confirm]);

  // Phase 2.3 — Barcode custody keys (CLAUDE.md standing rule 14, §18.6, ADR-0013):
  // Ctrl+S Save/Dispose · Alt+U Unlink · Esc Cancel/Clear — mirror the top/bottom
  // action bar (same handlers + gates), per the config-driven variant (Sample Out /
  // Disposal / Location).  Save + Unlink only exist on a write form (!readOnly); Esc
  // is allowInInput:false so it never clears the batch while typing a barcode.
  const hotkeys: HotkeyDef[] = [];
  if (!config.readOnly) {
    hotkeys.push(
      { combo: 'mod+s', label: config.actionLabel, group: config.breadcrumbLeaf, order: 1,
        run: () => save(), disabled: saving || rows.length === 0 || !canWrite },
      { combo: 'alt+u', label: 'Unlink', group: config.breadcrumbLeaf, order: 3,
        run: () => { void unlink(); }, disabled: selected.size === 0 || entryDisabled },
    );
  }
  if (config.historyRoute) {
    hotkeys.push({ combo: 'alt+h', label: 'History', group: config.breadcrumbLeaf, order: 5,
      run: () => navigate(config.historyRoute!) });
  }
  // Export to (Alt+X) — the labelled button lives on the grid header strip
  // (headerLeft); every variant (incl. read-only Location / History) can export.
  hotkeys.push({ combo: 'alt+x', label: 'Export to', group: config.breadcrumbLeaf, order: 7,
    run: () => exportRef.current?.open() });
  hotkeys.push({ combo: 'esc', label: config.readOnly ? 'Clear' : 'Cancel', group: config.breadcrumbLeaf,
    order: 9, allowInInput: false, run: () => { void cancel(); }, disabled: saving });
  useHotkeys(hotkeys);

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Sample Tracking</span><i className="ti ti-chevron-right" />
            <span>Barcode Sample Custody</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{config.breadcrumbLeaf}</span>
          </div>
          <div className="dp-title-row">
            {/* ONE combined action row (user request 2026-10-10): the load-time chip
                on the LEFT (standing rule 7 — at the top next to the title) and the
                form's actions (History / Save / Cancel) on the RIGHT — no separate
                white action card. It lives in the fixed .bl-top header, so the
                actions stay visible while the grid scrolls. The grid toolbar passes
                showMeta={false}. */}
            <span className="si-load-chip"><LoadTimeChip ms={loadMs} loading={loading} /></span>
            <div className="si-top-actions">
              {config.historyRoute && (
                <button className="ca-btn" onClick={() => navigate(config.historyRoute!)} title={`Disposal History (${formatCombo('alt+h')})`}>
                  <span className="tb-i">🕘</span> History
                </button>
              )}
              {!config.readOnly && (
                <button className="ca-btn ca-btn-solid" onClick={save}
                  disabled={saving || rows.length === 0 || !canWrite}
                  title={(!canWrite ? `You are not authorized to save on ${config.formName}.` : config.actionLabel) + ` (${formatCombo('mod+s')})`}>
                  {saving ? <><LoaderSymbol size={13} /> {config.successVerb}…</> : <><span className="tb-i">💾</span> {config.actionLabel}</>}
                </button>
              )}
              <button className="ca-btn" onClick={() => void cancel()} disabled={saving} title={(config.readOnly ? 'Clear' : 'Cancel') + ` (${formatCombo('esc')})`}>
                <span className="tb-i" style={{ color: 'var(--ai-blue, #185fa5)' }}>↩</span> {config.readOnly ? 'Clear' : 'Cancel'}
              </button>
            </div>
          </div>
          <div className="dp-subtitle">{config.subtitle} · {rows.length} scanned</div>
        </div>
      </div>

      <div className="bl-grid-area">
        {/* Barcode Scan band (no To picker for Out / Disposal). */}
        <div className="bl-card" style={{ padding: 12, marginBottom: 8 }}>
          {entryDisabled && (
            <div style={{ marginBottom: 10, fontSize: 12.5, color: '#8a5a00', background: '#fff7e6',
              border: '1px solid #ffd591', borderRadius: 4, padding: '7px 10px' }}>
              <span className="tb-i">🔒</span> You have read-only access to {config.breadcrumbLeaf} — data entry is disabled.
            </div>
          )}
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Barcode Scan</span>
            <input
              ref={barcodeRef}
              type="text"
              style={{ width: 380, maxWidth: '100%', flex: 'none', ...REQ_FIELD }}
              placeholder="Scan or type a bottle barcode…"
              value={barcode}
              disabled={scanning || entryDisabled}
              onChange={e => setBarcode(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void scan(); } }}
              onBlur={() => { if (barcode.trim()) void scan(); }}
            />
            {scanning && <LoaderSymbol size={14} />}
          </label>
        </div>

        {/* Unlink (write forms) + a labelled "Export to" live ON the grid's own
            header strip (headerLeft) — above the column headers, inside the grid card
            — matching legacy ALPACA's "Barcode Transaction" grid toolbar (owner
            decision 2026-10-10). hideHeaderExport suppresses the grid's built-in
            Export icon so there is ONE Export control; Maximize stays top-right
            (§18.6). A read-only form (Location / History) shows Export only. */}
        <div className="bl-card bl-card-flex">
          <AppDataGrid<CustodyRow>
            className="custody-grid"
            flushRight
            rows={rows}
            columns={columns}
            getRowId={r => r.id}
            initialSort={config.initialSort}
            selectable
            selectedIds={selected}
            onSelectionChange={setSelected}
            hideHeaderExport
            emptyText={config.emptyPrompt}
            headerLeft={
              <>
                {!config.readOnly && (
                  <button className="ca-btn" onClick={() => void unlink()} disabled={selected.size === 0 || entryDisabled} title={`Unlink selected (${formatCombo('alt+u')})`}>
                    <span className="tb-i">🔗</span> Unlink{selected.size > 0 ? ` (${selected.size})` : ''}
                  </button>
                )}
                <ExportMenu ref={exportRef} filename={config.exportFilename ?? config.pageId} {...gridExportMatrix(rows, columns)}
                  triggerClassName="ca-btn" title={`Export to (${formatCombo('alt+x')})`} />
              </>
            }
          />
        </div>
      </div>
    </div>
  );
}
